/*
  Your Private Edit (spec chapter 27; stage א5), on /pages/private-edit.
  <amor-edit> draws a result kept in the demo layer (DemoStore), so a refresh
  or going back never asks the model again:
  - ?id=E-1: an Edit made from the request field (amor-ai.js), with every
    version of its refining;
  - ?saved=S-1: an Edit saved to the account, as it was saved;
  - ?edit=saved: Noa's saved Edit, from the demo_customer metaobject.
  The items come from the store (window.AmorAI.loadItem): an item sold out
  since the server's catalog was built is left out, unless she owns it. An
  item from her wardrobe shows "already yours" and stays out of the bag and
  the total.
  Refining (three pills, or "add a detail") sends the current version to
  /edit and adds the new one; "back to the previous version" goes back one,
  in the browser only. Refining a saved Edit starts a new Edit from it: a
  saved Edit never changes. Saving needs signing in: a guest gets the login,
  and the Edit is saved right after it.
  Stage א9 (note 3): each look's view, on a model or on the avatar, comes from
  a ready pool (look-view.js), picked for all the looks at once when they are in.
*/
(() => {
  const REFINE_MAX = 100;
  const store = () => window.DemoStore;
  const ai = () => window.AmorAI;
  const normalize = (text) => String(text || '').replace(/\s+/g, ' ').trim();

  const seed = () => {
    try {
      return JSON.parse(document.getElementById('DemoCustomer')?.textContent || 'null');
    } catch {
      return null;
    }
  };

  /** Noa's saved Edit (demo_customer.saved_edit), in the shape /edit answers with. */
  const fromSeed = (saved) => ({
    status: 'ok',
    opening: saved.intro || '',
    question: null,
    looks: (saved.looks || []).map((look) => ({
      name: look.name,
      line: look.sentence,
      items: (look.items || []).map((item) => ({
        handle: item.product,
        color: item.color || null,
        reason: item.why || '',
        owned: item.owned === true,
      })),
    })),
    extras: (saved.more || []).map((item) => ({ handle: item.product, color: item.color || null, reason: item.why || '', owned: false })),
    gift_card: false,
    find_my_piece: false,
  });

  /** What /edit gets back as `previous` when refining: the items, by handle and color. */
  const previousOf = (result) => ({
    looks: (result.looks || []).map((look) => ({
      items: (look.items || []).map(({ handle, color, owned }) => ({ handle, color, owned: owned === true })),
    })),
    extras: (result.extras || []).map(({ handle, color }) => ({ handle, color })),
  });

  const sizedImage = (src, width) => (src ? src.replace(/([?&])width=\d+/, `$1width=${width}`) : '');

  const worldOf = (option) => (/גבר/.test(option) ? 'men' : /נש/.test(option) ? 'women' : null);

  class AmorEdit extends HTMLElement {
    connectedCallback() {
      this.body = this.querySelector('[data-edit-body]');
      try {
        this.strings = JSON.parse(this.querySelector('[data-edit-strings]')?.textContent || '{}');
      } catch {
        this.strings = {};
      }
      this.token = 0;
      this.looks = [];
      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('submit', (event) => this.onSubmit(event));
      document.addEventListener('demo-store:change', (event) => {
        if (event.detail?.key !== 'signedIn' || !store()?.signedIn() || !this.pendingSave) return;
        this.pendingSave = false;
        this.save();
      });
      this.open();
    }

    open() {
      const params = new URLSearchParams(window.location.search);
      this.record = null;
      this.saved = null;
      if (params.get('id')) {
        this.record = store()?.edit(params.get('id')) || null;
      } else if (params.get('saved')) {
        this.saved = store()?.savedEdit(params.get('saved')) || null;
      } else if (params.get('edit') === 'saved') {
        const saved = seed()?.savedEdit;
        if (saved) this.saved = { id: 'noa', text: saved.request || '', image: false, mode: 'occasion', result: fromSeed(saved) };
      }
      if (!this.record && !this.saved) {
        this.renderEmpty();
        return;
      }
      this.draw();
    }

    /** The request: the working Edit, or the saved one being viewed. */
    get request() {
      return this.record || this.saved;
    }

    get result() {
      return this.record ? this.record.versions[this.record.current] : this.saved.result;
    }

    get isSaved() {
      if (this.saved) return true;
      return this.record?.savedId != null && this.record.savedVersion === this.record.current;
    }

    onClick(event) {
      const pill = event.target.closest('[data-edit-refine-pill]');
      if (pill) {
        this.refine(pill.textContent);
        return;
      }
      if (event.target.closest('[data-edit-back]')) {
        this.back();
        return;
      }
      if (event.target.closest('[data-edit-retry]')) {
        this.retry?.();
        return;
      }
      const option = event.target.closest('[data-edit-option]');
      if (option) {
        this.answerQuestion(option.dataset.editOption);
        return;
      }
      const add = event.target.closest('[data-look-add-sheet]');
      if (add) {
        this.openSheet(Number(add.dataset.lookIndex), add);
        return;
      }
      const save = event.target.closest('[data-edit-save]');
      if (save) this.save(save);
    }

    onSubmit(event) {
      const form = event.target.closest('[data-edit-detail]');
      if (!form) return;
      event.preventDefault();
      const input = form.querySelector('input');
      if (!normalize(input.value)) {
        input.focus();
        return;
      }
      this.refine(input.value);
    }

    /* --- Drawing ------------------------------------------------------------- */

    template(selector) {
      const template = this.querySelector(`template[${selector}]`);
      return template ? document.importNode(template.content, true) : document.createDocumentFragment();
    }

    showRequest() {
      const request = this.request;
      const line = this.querySelector('[data-edit-request]');
      const text = this.querySelector('[data-edit-text]');
      const thumb = this.querySelector('[data-edit-thumb]');
      const change = this.querySelector('[data-edit-change]');
      if (!line) return;
      line.hidden = !request;
      if (!request) return;
      text.textContent = normalize(request.text) ? `«${normalize(request.text)}»` : this.strings.imageOnly || '';
      const small = request.image && this.record ? store().thumb(this.record.id) : null;
      thumb.hidden = !small;
      if (small) thumb.src = small;
      // "Edit" puts the request back in the field on the AMOR BY YOU page, without sending it.
      const url = new URL(change.getAttribute('href').split('#')[0], window.location.origin);
      url.searchParams.set('draft', normalize(request.text));
      change.href = `${url.pathname}${url.search}#AmorRequest`;
    }

    renderEmpty() {
      this.showRequest();
      this.body.replaceChildren(this.template('data-edit-state="empty"'));
    }

    renderLoading(status) {
      const content = this.template('data-edit-state="loading"');
      const line = content.querySelector('[data-edit-status]');
      if (line) line.hidden = !status;
      return content;
    }

    /**
     * Draws the current version, with its items from the store. Every item,
     * the looks' and the extras', starts loading at once, and each look shows
     * as soon as its own items are in; until then it keeps a skeleton.
     * Resolves when everything is in.
     */
    async draw() {
      const token = ++this.token;
      this.showRequest();
      const result = this.result;
      const looks = (result.looks || []).map((look) =>
        Promise.all((look.items || []).map((item) => ai().loadItem(item))).then((items) => ({ ...look, items: items.filter(Boolean) }))
      );
      const extras = Promise.all((result.extras || []).map((item) => ai().loadItem(item))).then((items) => items.filter(Boolean));
      // By look index; a look none of whose items is left stays null.
      this.looks = looks.map(() => null);
      this.views = looks.map(() => null);
      this.renderResult(result);
      const slots = [...this.querySelectorAll('[data-edit-look-slot]')];
      const looksIn = Promise.all(
        looks.map((load, index) =>
          load.then((look) => {
            if (token !== this.token) return;
            if (!look.items.length) {
              slots[index]?.remove();
              return;
            }
            this.looks[index] = look;
            slots[index]?.replaceWith(this.buildLook(look, index));
            this.syncHearts();
          })
        )
      );
      await Promise.all([
        // Stage א9 (note 3): the views, once every look is in, so two looks never share one.
        looksIn.then(() => {
          if (token === this.token) this.showViews();
        }),
        extras.then((items) => {
          if (token !== this.token) return;
          this.renderExtras(items);
        }),
      ]);
      if (token === this.token) this.settle();
    }

    /** Each look's view from the pool, in the looks' order (look-view.js). */
    showViews() {
      window.AmorLookView?.assign(
        this.looks
          .map((look, index) => look && { element: this.views[index], name: look.name, items: look.items })
          .filter(Boolean)
      );
    }

    /** The cards' hearts follow the wishlist. */
    syncHearts() {
      document.dispatchEvent(new CustomEvent('demo-store:change', { detail: { key: 'wishlist' } }));
    }

    /** The page around the looks, at once: a skeleton in place of each look until its items are in. */
    renderResult(result) {
      const content = this.template('data-edit-state="result"');
      const find = (name) => content.querySelector(`[${name}]`);
      const status = result.status;
      const request = this.request;
      // Until the items are in, a look is expected if it has items; settle() corrects it after.
      const hasLooks = (result.looks || []).some((look) => (look.items || []).length);

      find('data-edit-opening').textContent = status === 'bad_image' ? this.strings.badImage : result.opening || '';
      find('data-edit-image-note').hidden = !(request.image && hasLooks);

      const canGoBack = Boolean(this.record && this.record.current > 0);
      find('data-edit-refine').hidden = !(hasLooks || canGoBack);
      find('data-edit-back').hidden = !canGoBack;

      if (status === 'clarify' && result.question) {
        find('data-edit-question').hidden = false;
        find('data-edit-question-text').textContent = result.question.text || '';
        (result.question.options || []).forEach((text) => {
          const pill = document.createElement('button');
          pill.type = 'button';
          pill.className = 'pill';
          pill.dataset.editOption = text;
          pill.textContent = text;
          find('data-edit-options').append(pill);
        });
      }

      // One slot per look, by index; draw() replaces it with the look, or removes it.
      // Stage א8 (note 6.1): the first look above the refine row, the others under it.
      const looks = find('data-edit-looks');
      const rest = find('data-edit-looks-rest');
      (result.looks || []).forEach((look, index) => {
        const slot = this.renderLoading(false).firstElementChild;
        slot.dataset.editLookSlot = '';
        (index === 0 ? looks : rest).append(slot);
      });

      find('data-edit-gift').hidden = result.gift_card !== true;
      if (result.find_my_piece === true) {
        find('data-edit-find').hidden = false;
        const link = find('data-edit-find-link');
        link.href = `${link.getAttribute('href')}?q=${encodeURIComponent(normalize(request.text))}`;
      }
      find('data-edit-examples').hidden = status !== 'unrelated';

      const save = find('data-edit-save');
      const saved = find('data-edit-saved');
      save.hidden = !hasLooks || this.isSaved;
      saved.hidden = !this.isSaved;

      this.body.replaceChildren(content);
    }

    renderExtras(items) {
      const more = this.querySelector('[data-edit-more]');
      if (!more || !items.length) return;
      const list = more.querySelector('[data-edit-more-list]');
      items.forEach((item) => list.append(this.buildItem(item)));
      more.hidden = false;
      this.syncHearts();
    }

    /** Once every look is in: what depends on having at least one. */
    settle() {
      const hasLooks = this.looks.some(Boolean);
      const canGoBack = Boolean(this.record && this.record.current > 0);
      const find = (name) => this.querySelector(`[${name}]`);
      find('data-edit-image-note').hidden = !(this.request.image && hasLooks);
      find('data-edit-refine').hidden = !(hasLooks || canGoBack);
      find('data-edit-save').hidden = !hasLooks || this.isSaved;
    }

    buildItem(item) {
      const node = this.template('data-edit-item').firstElementChild;
      const card = item.card();
      if (card) {
        if (item.owned) {
          card.classList.add('card--owned');
          card.querySelector('.card__badge')?.remove();
          const badge = document.createElement('span');
          badge.className = 'badge label card__badge edit-item__owned';
          badge.textContent = this.strings.owned;
          card.querySelector('.card__media')?.append(badge);
        }
        node.querySelector('[data-item-card]').replaceWith(card);
      }
      const why = node.querySelector('[data-item-why]');
      if (item.reason) why.textContent = item.reason;
      else why.remove();
      return node;
    }

    buildLook(look, index) {
      const node = this.template('data-edit-look').firstElementChild;
      node.querySelector('[data-look-name]').textContent = look.name || '';
      node.querySelector('[data-look-line]').textContent = look.line || '';

      // Stage א9 (note 3): the look's view; showViews() fills it once every look is in.
      this.views[index] = node.querySelector('amor-look-view');

      const list = node.querySelector('[data-look-items]');
      look.items.forEach((item) => list.append(this.buildItem(item)));

      // Items she already owns stay out of the bag, the total and the fitting.
      const addable = look.items.filter((item) => !item.owned && item.available);
      const total = addable.reduce((sum, item) => sum + item.price, 0);
      node.querySelector('[data-look-total]').textContent = ai().formatMoney(total, this.dataset.priceFormat);
      const add = node.querySelector('[data-look-add-sheet]');
      add.dataset.lookIndex = index;
      node.querySelector('[data-look-fitting]').href = ai().fittingUrl(addable);
      if (!addable.length) {
        node.querySelector('.edit-look__total').hidden = true;
        node.querySelector('.edit-look__buttons').hidden = true;
        node.querySelector('[data-look-all-owned]').hidden = false;
      }
      return node;
    }

    /* --- Add the look to the bag: the size sheet, with her sizes chosen -------- */

    openSheet(index, trigger) {
      const look = this.looks[index];
      if (!look) return;
      ai().openSizeSheet(
        look.items.filter((item) => !item.owned && item.available),
        trigger
      );
    }

    /* --- Refining ------------------------------------------------------------ */

    /** A saved Edit never changes: refining it starts a working Edit from it. */
    working() {
      if (this.record) return this.record;
      const saved = this.saved;
      this.record = store().addEdit({
        text: saved.text || '',
        mode: saved.mode || 'occasion',
        budget: saved.budget || 0,
        world: saved.world || null,
        image: saved.image === true,
        versions: [saved.result],
        current: 0,
        savedId: saved.id,
        savedVersion: 0,
      });
      this.saved = null;
      window.history.replaceState({}, '', `${window.location.pathname}?id=${encodeURIComponent(this.record.id)}`);
      return this.record;
    }

    /** The request in words for /edit. The image is never kept, so an image request goes on in words. */
    requestBody() {
      const record = this.record;
      return {
        text: normalize(record.text) || this.strings.imageFallback,
        mode: record.mode === 'image' ? 'occasion' : record.mode,
        budget: record.budget || 0,
        world: record.world || null,
      };
    }

    async refine(text) {
      const value = normalize(text).slice(0, REFINE_MAX);
      if (!value || this.busy) return;
      const base = this.result;
      this.working();
      this.retry = () => this.refine(value);
      await this.ask({ ...this.requestBody(), previous: previousOf(base), refine: value });
    }

    async answerQuestion(option) {
      if (this.busy) return;
      this.working();
      this.retry = () => this.answerQuestion(option);
      const world = worldOf(option) || this.record.world;
      this.record = store().updateEdit(this.record.id, { world });
      await this.ask({ ...this.requestBody(), answer: option, world });
    }

    /** The looks give way to the loading state in place; the new version is added and shown. */
    async ask(body) {
      this.busy = true;
      this.token += 1;
      // Both looks give way to a skeleton in place. "Choosing" shows in the refine row's status line,
      // on its surface (stage א10, note 2); with no refine row (answering a question), over the skeleton.
      const refine = this.querySelector('[data-edit-refine]');
      const inRefine = Boolean(refine && !refine.hidden);
      this.querySelector('[data-edit-looks]')?.replaceChildren(this.renderLoading(false));
      this.querySelector('[data-edit-looks-rest]')?.replaceChildren(this.renderLoading(!inRefine));
      if (inRefine) this.showRefineStatus(this.strings.choosing);
      this.querySelectorAll('[data-edit-more], .edit__end, [data-edit-gift], [data-edit-find]').forEach((element) => (element.hidden = true));
      this.querySelectorAll('[data-edit-refine] button, [data-edit-refine] input').forEach((control) => (control.disabled = true));
      let result;
      try {
        result = await ai().edit(body);
      } catch (error) {
        this.busy = false;
        await this.draw();
        this.showRefineError(error.code);
        return;
      }
      const record = this.record;
      const versions = [...record.versions.slice(0, record.current + 1), result];
      this.record = store().updateEdit(record.id, { versions, current: versions.length - 1 });
      this.busy = false;
      await this.draw();
    }

    /** The refine row's status line: "choosing" while waiting, or an error (tone="error"). */
    showRefineStatus(text, tone) {
      const status = this.querySelector('[data-edit-refine-status]');
      if (!status) return null;
      this.querySelector('[data-edit-refine]').hidden = false;
      status.hidden = false;
      if (tone) status.dataset.tone = tone;
      else delete status.dataset.tone;
      status.querySelector('[data-edit-refine-text]').textContent = text;
      status.querySelector('[data-edit-retry]').hidden = true;
      status.querySelector('[data-edit-refine-worlds]').hidden = true;
      return status;
    }

    showRefineError(code) {
      // Over her own limit: wait a few minutes, with Women and Men and no "try again" (stage א7).
      const limited = code === 'rate_limited';
      const status = this.showRefineStatus(limited ? this.strings.rateLimited : this.strings.error, 'error');
      if (!status) return;
      status.querySelector('[data-edit-retry]').hidden = !ai().retryable(code);
      // Women and Men also under the site's daily limit (site_limit), like the answer card (full pass, stage א7).
      status.querySelector('[data-edit-refine-worlds]').hidden = !['rate_limited', 'site_limit'].includes(code);
    }

    back() {
      if (!this.record || this.record.current < 1 || this.busy) return;
      this.record = store().updateEdit(this.record.id, { current: this.record.current - 1 });
      this.draw();
    }

    /* --- Saving to the account ------------------------------------------------ */

    save(trigger) {
      if (this.isSaved || this.busy) return;
      if (!store()?.signedIn()) {
        this.pendingSave = true;
        document.querySelector('amor-login')?.open(trigger || null, null);
        return;
      }
      const request = this.request;
      const thumbs = this.looks
        .filter(Boolean)
        .flatMap((look) => look.items)
        .map((item) => sizedImage(item.image, 160))
        .filter(Boolean)
        .slice(0, 8);
      const saved = store().addSavedEdit({
        text: request.text || '',
        image: request.image === true,
        mode: request.mode || 'occasion',
        budget: request.budget || 0,
        world: request.world || null,
        result: this.result,
        thumbs,
      });
      if (this.record) this.record = store().updateEdit(this.record.id, { savedId: saved.id, savedVersion: this.record.current });
      const button = this.querySelector('[data-edit-save]');
      const done = this.querySelector('[data-edit-saved]');
      if (button) button.hidden = true;
      if (done) done.hidden = false;
    }
  }

  customElements.define('amor-edit', AmorEdit);
})();
